import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, HardHat, Wrench, Factory, Truck, Mountain, Users, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const sectors = [
  {
    icon: Building2,
    name: 'Construction',
    image: 'https://images.unsplash.com/photo-1743130960579-f88d04835413?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwcHJvZmVzc2lvbmFsJTIwd29ya2VycyUyMGF1c3RyYWxpYXxlbnwwfHx8fDE3NzM1MzkwMDV8MA&ixlib=rb-4.1.0&q=85',
    description: 'From residential developments to major commercial projects, we recruit skilled professionals for construction businesses of all sizes.',
    roles: ['Site Managers', 'Project Managers', 'Forepersons', 'Supervisors', 'Contract Administrators', 'Estimators', 'Safety Officers', 'Labourers']
  },
  {
    icon: HardHat,
    name: 'Engineering',
    image: 'https://images.unsplash.com/photo-1765378025255-5c2ff04563f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxjaXZpbCUyMGVuZ2luZWVyJTIwbG9va2luZyUyMGF0JTIwYmx1ZXByaW50cyUyMG9uJTIwY29uc3RydWN0aW9uJTIwc2l0ZXxlbnwwfHx8fDE3NzM1MzkwMTl8MA&ixlib=rb-4.1.0&q=85',
    description: 'Supporting engineering firms and contractors with qualified professionals across civil, structural, mechanical and electrical disciplines.',
    roles: ['Civil Engineers', 'Structural Engineers', 'Project Engineers', 'Design Engineers', 'CAD Drafters', 'Engineering Managers', 'Graduate Engineers']
  },
  {
    icon: Wrench,
    name: 'Trades',
    image: 'https://images.unsplash.com/photo-1734888369524-bd915004d0c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHx3ZWxkZXIlMjBpbmR1c3RyaWFsJTIwd29ya2VyJTIwc3BhcmtzJTIwcHJvdGVjdGlvbiUyMG1hc2t8ZW58MHx8fHwxNzczNTM5MDIwfDA&ixlib=rb-4.1.0&q=85',
    description: 'Qualified tradespeople for construction, fit-out, maintenance and service work across residential, commercial and industrial settings.',
    roles: ['Electricians', 'Plumbers', 'Carpenters', 'Welders', 'Boilermakers', 'Fitters', 'HVAC Technicians', 'Painters']
  },
  {
    icon: Factory,
    name: 'Industrial & Manufacturing',
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800',
    description: 'Staff for manufacturing plants, warehouses, and industrial facilities. From production workers to maintenance specialists.',
    roles: ['Maintenance Fitters', 'Machine Operators', 'Production Workers', 'Warehouse Staff', 'Quality Controllers', 'Process Workers', 'Team Leaders']
  },
  {
    icon: Truck,
    name: 'Infrastructure',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
    description: 'Supporting major infrastructure projects including roads, rail, utilities and public works across Australia.',
    roles: ['Civil Supervisors', 'Road Workers', 'Plant Operators', 'Traffic Controllers', 'Linesmen', 'Utility Workers', 'Project Coordinators']
  },
  {
    icon: Mountain,
    name: 'Mining Support',
    image: 'https://images.unsplash.com/photo-1746349086423-06ea6b4d73f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxoZWF2eSUyMGV4Y2F2YXRvciUyMG1pbmluZyUyMG1hY2hpbmVyeSUyMGF1c3RyYWxpYXxlbnwwfHx8fDE3NzM1MzkwMTl8MA&ixlib=rb-4.1.0&q=85',
    description: 'Site services and support roles for the mining industry, from equipment maintenance to camp operations.',
    roles: ['Heavy Diesel Mechanics', 'Auto Electricians', 'Boilermakers', 'Maintenance Planners', 'Warehouse Coordinators', 'Camp Staff']
  },
  {
    icon: Users,
    name: 'Project Support',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800',
    description: 'Office-based and site support roles that keep construction and engineering projects running smoothly.',
    roles: ['Project Coordinators', 'Document Controllers', 'Admin Assistants', 'Schedulers', 'Cost Controllers', 'Procurement Officers']
  },
  {
    icon: Briefcase,
    name: 'Management',
    image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800',
    description: 'Leadership and senior management positions across construction, engineering and industrial businesses.',
    roles: ['General Managers', 'Operations Managers', 'Construction Managers', 'Engineering Managers', 'HSE Managers', 'Commercial Managers']
  }
];

export default function Sectors() {
  return (
    <div data-testid="sectors-page">
      <SEO page="sectors" />
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-4">Our Sectors</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase mb-6">
              Industry Expertise That Delivers Results
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              We specialise in recruitment for the industries that build Australia. Our focused approach means we understand the specific skills, qualifications and experience you need.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-16">
            {sectors.map((sector, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`grid lg:grid-cols-2 gap-8 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative h-[300px] md:h-[400px] overflow-hidden">
                    <img 
                      src={sector.image} 
                      alt={sector.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <sector.icon className="w-12 h-12 text-white" />
                    </div>
                  </div>
                </div>
                <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase mb-4">
                    {sector.name}
                  </h2>
                  <p className="text-slate-600 text-lg leading-relaxed mb-6">
                    {sector.description}
                  </p>
                  <div className="mb-6">
                    <p className="font-semibold text-slate-700 mb-3">Typical roles we recruit:</p>
                    <div className="flex flex-wrap gap-2">
                      {sector.roles.map((role, rIdx) => (
                        <span key={rIdx} className="bg-slate-100 text-slate-700 px-3 py-1 text-sm">
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#1A8B9D]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-6">
              Need Talent For Your Sector?
            </h2>
            <p className="text-teal-100 text-lg mb-10">
              Whether you're hiring for a single role or building an entire team, we can help you find the right people.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/employers#request-staff">
                <Button className="bg-white hover:bg-slate-100 text-[#1A8B9D] font-bold py-6 px-10 uppercase tracking-wide text-base">
                  Request Staff
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="bg-[#F5A623] hover:bg-[#D98E15] text-slate-900 font-bold py-6 px-10 uppercase tracking-wide text-base">
                  Contact Us <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
